// Workflow versions (SPEC §7): draft -> live -> retired, one live version per workflow. Publishing a draft retires
// the old live version. A draft must pass the checks before it can go live.
import {
  KIND_NODES,
  lenderApiPorts,
  NodeType,
  NODE_PORTS,
  whatsappStepProblems,
  type AddWorkflowNodeInput,
  type CreateWorkflowInput,
  type WorkflowDocument,
  type WorkflowKind,
} from '../../schemas'
import { refuse } from '../refuse'
import type { State, VersionRecord, WorkflowRecord } from '../state'
import { newId } from './ids'
import { today } from './sending-window'
import { playable } from './voice-messages'
import { notArchived } from './workflow-state'

// wider than the kit canvas card (224 px), so neighbouring columns never cover each other's port labels
const COLUMN_PX = 280
const ROW_PX = 90

export function liveVersion(state: State, workflowId: string): VersionRecord | null {
  return state.versions.find((v) => v.workflow_id === workflowId && v.status === 'live') ?? null
}

/** Places the nodes left to right by their distance from the trigger (as the demo draws them). */
export function layoutByDepth(document: WorkflowDocument): WorkflowDocument {
  const depth = new Map<string, number>()
  const start = document.nodes.find((n) => n.type.startsWith('trigger.')) ?? document.nodes[0]
  const queue: [string, number][] = start ? [[start.id, 0]] : []
  while (queue.length) {
    const [id, d] = queue.shift()!
    if (depth.has(id)) continue
    depth.set(id, d)
    for (const edge of document.edges.filter((e) => e.from === id)) queue.push([edge.to, d + 1])
  }
  const rowsUsed = new Map<number, number>()
  const nodes = document.nodes.map((node) => {
    const column = depth.get(node.id) ?? 0
    const row = rowsUsed.get(column) ?? 0
    rowsUsed.set(column, row + 1)
    return { ...node, pos: { x: 20 + column * COLUMN_PX, y: 20 + row * ROW_PX } }
  })
  return { nodes, edges: document.edges }
}

/** What stops a document from going live; empty = the checks pass. Each kind has its own trigger and nodes. */
export function documentProblems(document: WorkflowDocument, kind: WorkflowKind = 'campaign'): string[] {
  const problems: string[] = []
  const ids = new Set(document.nodes.map((n) => n.id))
  const allowed = KIND_NODES[kind]
  const triggers = document.nodes.filter((n) => n.type === allowed[0])
  if (triggers.length !== 1)
    problems.push(
      kind === 'campaign'
        ? 'A workflow needs exactly one audience trigger.'
        : 'A classification workflow needs exactly one "new data" trigger.'
    )
  for (const node of document.nodes) {
    const type = NodeType.safeParse(node.type)
    if (!type.success) {
      problems.push(`"${node.name}" has an unknown node type.`)
      continue
    }
    if (!allowed.includes(type.data)) {
      problems.push(`"${node.name}" is not a ${kind} node.`)
      continue
    }
    const out = document.edges.filter((e) => e.from === node.id)
    if (NODE_PORTS[type.data].length > 0 && out.length === 0) problems.push(`"${node.name}" leads nowhere.`)
    for (const edge of out) {
      // "always" (the editor offers it) takes every outcome that has no line of its own
      if (edge.when !== 'always' && !NODE_PORTS[type.data].includes(edge.when))
        problems.push(`"${node.name}" has no port "${edge.when}".`)
    }
  }
  for (const edge of document.edges) {
    if (!ids.has(edge.from) || !ids.has(edge.to))
      problems.push('An arrow points to a node that is not there.')
  }
  return problems
}

const IVR_CALL = NodeType.enum['campaign.ivr_call']

/**
 * What the portal's data says against a document: an IVR node must play an approved voice message (its words are a
 * registered consent wording, N1) and its yes and stop digits must differ.
 */
/** A list check names a list there is (Data → Lists), a "put in batch" a batch there is: a typed name is no list. */
export function choiceProblems(state: State, document: WorkflowDocument): string[] {
  const problems: string[] = []
  for (const node of document.nodes) {
    if (node.type === 'classify.list_check' && !state.lists.some((l) => l.list_key === node.cfg.list))
      problems.push(
        `"${node.name}" checks a list that is not there: make it under Data → Lists, then pick it.`
      )
    if (node.type === 'classify.put_in_batch' && !state.batches.some((b) => b.id === node.cfg.batch))
      problems.push(
        `"${node.name}" puts people in a batch that is not there: make it under Data → Batches, then pick it.`
      )
  }
  return problems
}

export function ivrProblems(state: State, document: WorkflowDocument): string[] {
  const problems: string[] = []
  for (const node of document.nodes.filter((n) => n.type === IVR_CALL)) {
    const message = state.voiceMessages.find((m) => m.id === node.cfg.message)
    if (!message || !playable(message))
      problems.push(`"${node.name}" plays a voice message that is not approved (not registered in the CMP).`)
    if (node.cfg.yes_digit !== undefined && node.cfg.yes_digit === node.cfg.stop_digit)
      problems.push(`"${node.name}": the yes digit and the stop digit must differ.`)
  }
  return problems
}

/** Removes a draft that is not the workflow's only version. */
export function discardDraft(state: State, versionId: string): VersionRecord {
  const draft = draftOf(state, versionId)
  if (state.versions.filter((v) => v.workflow_id === draft.workflow_id).length === 1)
    refuse('This is the only version: delete the workflow instead.')
  state.versions = state.versions.filter((v) => v.id !== draft.id)
  return draft
}

function draftOf(state: State, versionId: string): VersionRecord {
  const version = state.versions.find((v) => v.id === versionId)
  if (!version) refuse('That version is not there.')
  notArchived(state, version.workflow_id)
  if (version.status !== 'draft') refuse('Only a draft can be changed.')
  return version
}

export function newDraft(state: State, workflowId: string): VersionRecord {
  notArchived(state, workflowId)
  const versions = state.versions.filter((v) => v.workflow_id === workflowId)
  const base = liveVersion(state, workflowId) ?? versions[versions.length - 1]
  if (!base) refuse('This workflow has no version to copy.')
  const draft: VersionRecord = {
    id: newId(state, 'v'),
    workflow_id: workflowId,
    version: Math.max(...versions.map((v) => v.version)) + 1,
    status: 'draft',
    published_by: null,
    published_at: null,
    document: structuredClone(base.document),
  }
  state.versions.push(draft)
  return draft
}

export function saveDraft(state: State, versionId: string, document: WorkflowDocument): VersionRecord {
  const draft = draftOf(state, versionId)
  draft.document = structuredClone(document)
  return draft
}

const LENDER_API = NodeType.enum['campaign.http_preset']

/** The port the new node goes on by first (IVR yes, condition yes, dedupe pass, prefill link, else next); none for an End. */
export function firstPortOf(state: State, input: AddWorkflowNodeInput): string | undefined {
  if (input.type !== LENDER_API) return NODE_PORTS[input.type][0]
  // as the run engine does: a dedupe answers pass / fail, a prefill link / no_link, every other kind next
  const route = state.hubRoutes.find((r) => r.id === input.cfg.route)
  return lenderApiPorts(route?.kind)[0]
}

/**
 * Puts a new node on a port of `after`; what was on that port now comes after the new node, on its first port.
 * An End has no ports: the old target is then left unwired (the checks say so if nothing else reaches it).
 */
export function addNodeAfter(state: State, versionId: string, input: AddWorkflowNodeInput): VersionRecord {
  const draft = draftOf(state, versionId)
  const document = draft.document
  if (!document.nodes.some((n) => n.id === input.after)) refuse('Pick the node it goes after.')
  const kind = state.workflows.find((w) => w.id === draft.workflow_id)?.kind ?? 'campaign'
  if (!KIND_NODES[kind].includes(input.type)) refuse(`A ${kind} workflow cannot use that node.`)
  const id = newId(state, 'n')
  const old = document.edges.find((e) => e.from === input.after && e.when === input.port)
  if (old) {
    const port = firstPortOf(state, input)
    if (port) document.edges.push({ id: `e-${id}-${old.to}-${port}`, from: id, to: old.to, when: port })
    old.to = id
    old.id = `e-${input.after}-${id}-${input.port}`
  } else
    document.edges.push({
      id: `e-${input.after}-${id}-${input.port}`,
      from: input.after,
      to: id,
      when: input.port,
    })
  document.nodes.push({ id, type: input.type, name: input.name, cfg: input.cfg, pos: { x: 0, y: 0 } })
  draft.document = layoutByDepth(document)
  return draft
}

export function publishVersion(state: State, versionId: string, userId: string): VersionRecord {
  const draft = draftOf(state, versionId)
  const kind = state.workflows.find((w) => w.id === draft.workflow_id)?.kind ?? 'campaign'
  const problems = [
    ...documentProblems(draft.document, kind),
    ...ivrProblems(state, draft.document),
    ...choiceProblems(state, draft.document),
    ...whatsappStepProblems(draft.document, state.templates),
  ]
  if (problems.length) refuse('Fix the checks first: ' + problems[0])
  for (const version of state.versions) {
    if (version.workflow_id === draft.workflow_id && version.status === 'live') version.status = 'retired'
  }
  draft.status = 'live'
  draft.published_by = userId
  draft.published_at = today(state)
  return draft
}

/** The first draft of a new workflow: its kind's trigger, then an End. */
const STARTER: Record<WorkflowKind, WorkflowDocument> = {
  campaign: {
    nodes: [
      { id: 'n1', type: 'trigger.audience', name: 'Audience', cfg: {}, pos: { x: 0, y: 0 } },
      { id: 'n2', type: 'campaign.end', name: 'End', cfg: { outcome: 'done' }, pos: { x: 0, y: 0 } },
    ],
    edges: [{ id: 'e-n1-n2-next', from: 'n1', to: 'n2', when: 'next' }],
  },
  classification: {
    nodes: [
      {
        id: 'n1',
        type: 'trigger.new_data',
        name: 'New data',
        cfg: { when: 'arrival', sources: 'any', hour: 8 },
        pos: { x: 0, y: 0 },
      },
      { id: 'n2', type: 'classify.end', name: 'End', cfg: {}, pos: { x: 0, y: 0 } },
    ],
    edges: [{ id: 'e-n1-n2-next', from: 'n1', to: 'n2', when: 'next' }],
  },
}

export function createWorkflow(state: State, input: CreateWorkflowInput): WorkflowRecord {
  if (state.workflows.some((w) => w.name.toLowerCase() === input.name.toLowerCase()))
    refuse('A workflow with that name is there already.')
  const workflow: WorkflowRecord = { id: newId(state, 'wf'), name: input.name, kind: input.kind }
  state.workflows.push(workflow)
  state.versions.push({
    id: newId(state, 'v'),
    workflow_id: workflow.id,
    version: 1,
    status: 'draft',
    published_by: null,
    published_at: null,
    document: layoutByDepth(structuredClone(STARTER[input.kind])),
  })
  return workflow
}
